/**
 * The store behind `useLoader` (`./use-loader`): one entry per key, shared by every loader with that
 * key. React-free, so its rules (shared requests, newest answer wins, abort, cache, invalidate, poll)
 * are tested on their own in `loader-store.test.ts`.
 *
 * - A key a caller chooses (`useLoader(..., { key })`) is shared and cached: it starts with the project
 *   id (`${projectId}:tasks:...`) so `invalidateLoaders(projectId + ':tasks')` reaches it. Its data
 *   stays after the last loader using it unmounts, so coming back shows it at once and revalidates
 *  .
 * - A loader without a key gets a private one per set of inputs (`localLoaderKey`). Those are never
 *   cached or invalidated, and are dropped (their request aborted) once nothing uses them.
 */

/** Loads one key's data. Pass `signal` to fetchApi (`{ signal }`) so a superseded request stops. */
export type LoadFn<T> = (signal: AbortSignal) => Promise<T>;

export type LoaderSnapshot<T = unknown> = {
  /** Data has landed for this key (from the server or `setData`). */
  hasData: boolean;
  data: T | undefined;
  /** The last load for this key failed (the data, if any, stays). */
  error: unknown;
  /** A load has finished for this key and no `reload()` is waiting: `loading` is false. */
  settled: boolean;
  /** A request for this key is running (first load, reload, refresh, revalidate or poll). */
  fetching: boolean;
  /** When the data last came from the server (epoch ms), for "as of" times. */
  updatedAt: number | undefined;
  /** When the last load finished, data or error (epoch ms); polls count from here. */
  settledAt: number | undefined;
};

/** How a load was started. `reload` makes `loading` true again until it lands; the others don't. */
export type FetchMode = 'reload' | 'refresh' | 'background';

type Deferred = {
  promise: Promise<unknown>;
  resolve: (value: unknown) => void;
  reject: (error: unknown) => void;
};

type InFlight = {
  seq: number;
  mode: FetchMode;
  controller: AbortController;
  done: Deferred;
  superseded: boolean;
  /** Started by prefetch(): runs to the end even though nothing uses the key yet. */
  prefetch: boolean;
};

type Entry = {
  key: string;
  local: boolean;
  snapshot: LoaderSnapshot;
  listeners: Set<() => void>;
  users: number;
  /**
   * Each user's loader, newest last. A getter answers null once its loader has moved on to another
   * key, so a shared key is never loaded with another key's loader.
   */
  loaders: Map<object, () => LoadFn<unknown> | null>;
  /** prefetch()'s loader, for when no user has one. */
  prefetchLoad: LoadFn<unknown> | null;
  inFlight: InFlight | null;
  seq: number;
  /** Invalidated while nothing used it: the next user loads again whatever `staleMs` says. */
  stale: boolean;
  lastUsed: number;
  idleTimer: ReturnType<typeof setTimeout> | null;
};

const EMPTY: LoaderSnapshot = Object.freeze({
  hasData: false,
  data: undefined,
  error: undefined,
  settled: false,
  fetching: false,
  updatedAt: undefined,
  settledAt: undefined,
}) as LoaderSnapshot;

const LOCAL_PREFIX = '\u0000local:';

/** The private key of a loader without one: its instance and which set of inputs. */
export function localLoaderKey(instance: string, version: number): string {
  return `${LOCAL_PREFIX}${instance}:${version}`;
}

function deferred(): Deferred {
  let resolve!: (value: unknown) => void;
  let reject!: (error: unknown) => void;
  const promise = new Promise<unknown>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  // Nobody may be waiting (no refresh() caller): don't report an unhandled rejection.
  promise.catch(() => undefined);
  return { promise, resolve, reject };
}

export type LoaderStoreOptions = {
  /** Cached keys kept once nothing uses them; the least recently used go first. */
  maxIdleEntries?: number;
  now?: () => number;
};

export class LoaderStore {
  private entries = new Map<string, Entry>();
  private observers = new Set<(key: string, snapshot: LoaderSnapshot) => void>();
  private readonly maxIdle: number;
  private readonly now: () => number;

  constructor({ maxIdleEntries = 50, now = () => Date.now() }: LoaderStoreOptions = {}) {
    this.maxIdle = maxIdleEntries;
    this.now = now;
  }

  snapshot<T>(key: string): LoaderSnapshot<T> {
    return (this.entries.get(key)?.snapshot ?? EMPTY) as LoaderSnapshot<T>;
  }

  /**
   * Data remembered from an earlier visit (lib/launch-cache.ts): shown at once, and loaded again in
   * the background the first time a loader uses the key (no `settledAt`, so it's never fresh).
   * Ignored once the key has data of its own.
   */
  seed<T>(key: string, data: T, updatedAt: number): void {
    const entry = this.entry(key);
    if (entry.snapshot.hasData || entry.inFlight) return;
    this.update(entry, { hasData: true, data, error: undefined, settled: true, updatedAt, settledAt: undefined });
    this.scheduleIdle(entry);
  }

  /**
   * A shared key's answer that came from the server another way (a Board carries the project's task
   * meta): stored as if this key had loaded it now, so it counts as fresh. A request already running
   * for the key is newer, so it wins.
   */
  receive<T>(key: string, data: T): void {
    const entry = this.entry(key);
    if (entry.local || entry.inFlight) return;
    const at = this.now();
    entry.stale = false;
    this.update(entry, { hasData: true, data, error: undefined, settled: true, updatedAt: at, settledAt: at });
    this.scheduleIdle(entry);
  }

  /** Hear every shared key's changes (lib/launch-cache.ts remembers a few). Returns the stop. */
  observe(observer: (key: string, snapshot: LoaderSnapshot) => void): () => void {
    this.observers.add(observer);
    return () => {
      this.observers.delete(observer);
    };
  }

  /** For useSyncExternalStore: re-render when this key's snapshot changes. */
  listen(key: string, listener: () => void): () => void {
    const entry = this.entry(key);
    entry.listeners.add(listener);
    this.keepAlive(entry);
    return () => {
      entry.listeners.delete(listener);
      this.scheduleIdle(entry);
    };
  }

  /**
   * A mounted loader uses this key: load it unless it has data newer than `staleMs` (or a request
   * is already running, which it shares). Returns the release for the effect's cleanup.
   */
  use(
    key: string,
    getLoad: () => LoadFn<unknown> | null,
    { staleMs = 0 }: { staleMs?: number } = {}
  ) {
    const entry = this.entry(key);
    entry.users += 1;
    const token = {};
    entry.loaders.set(token, getLoad);
    this.keepAlive(entry);
    if (!entry.inFlight) {
      const { snapshot } = entry;
      const old = snapshot.settledAt === undefined || this.now() - snapshot.settledAt >= staleMs;
      if (!snapshot.hasData || entry.stale || old || snapshot.error !== undefined) {
        // With data to show, load behind it (`refreshing`); with none, it's a first load.
        void this.fetch(key, snapshot.hasData ? 'background' : 'reload');
      }
    }
    let released = false;
    return () => {
      if (released) return;
      released = true;
      entry.users -= 1;
      entry.loaders.delete(token);
      this.scheduleIdle(entry);
    };
  }

  /**
   * Start a request for the key now, replacing (aborting) one already running: the newest request
   * is the only one whose answer is stored. Resolves with the newest answer for the key, so a
   * caller whose request was replaced gets the result that replaced it.
   */
  fetch<T>(key: string, mode: FetchMode, { prefetch = false }: { prefetch?: boolean } = {}): Promise<T> {
    const entry = this.entries.get(key);
    const load = entry ? this.loaderFor(entry) : null;
    if (!entry || !load) return Promise.reject(new Error(`Nothing loads "${key}"`));

    const previous = entry.inFlight;
    const flight: InFlight = {
      seq: ++entry.seq,
      // A reload stays a reload when a refresh replaces it, or `loading` would end early.
      mode: previous?.mode === 'reload' && mode !== 'reload' ? 'reload' : mode,
      controller: new AbortController(),
      done: deferred(),
      superseded: false,
      prefetch,
    };
    if (previous) {
      previous.superseded = true;
      previous.controller.abort();
      previous.done.resolve(flight.done.promise);
    }
    entry.inFlight = flight;
    entry.stale = false;
    this.update(entry, {
      fetching: true,
      settled: flight.mode === 'reload' ? false : entry.snapshot.settled,
    });

    const current = () =>
      !flight.superseded && entry.inFlight === flight && this.entries.get(key) === entry;

    let started: Promise<unknown>;
    try {
      started = load(flight.controller.signal);
    } catch (error) {
      started = Promise.reject(error);
    }
    started.then(
      (data) => {
        if (current()) {
          entry.inFlight = null;
          const at = this.now();
          this.update(entry, {
            hasData: true,
            data,
            error: undefined,
            settled: true,
            fetching: false,
            updatedAt: at,
            settledAt: at,
          });
          // A prefetch nothing has shown yet: now it may be kept or dropped like any idle key.
          this.scheduleIdle(entry);
        }
        if (!flight.superseded) flight.done.resolve(data);
      },
      (error: unknown) => {
        if (current()) {
          entry.inFlight = null;
          this.update(entry, { error, settled: true, fetching: false, settledAt: this.now() });
          this.scheduleIdle(entry);
        }
        if (!flight.superseded) flight.done.reject(error);
      }
    );
    return flight.done.promise as Promise<T>;
  }

  /** Change a key's data locally (after saving one row). Every loader with the key sees it. */
  setData<T>(
    key: string,
    update: T | ((current: T | undefined) => T),
    shown?: T
  ): void {
    const entry = this.entry(key);
    // Before this key has data, the loader shows the previous inputs' (`shown`): edit that.
    const prev = (entry.snapshot.hasData ? entry.snapshot.data : shown) as T | undefined;
    const data =
      typeof update === 'function' ? (update as (current: T | undefined) => T)(prev) : update;
    this.update(entry, {
      hasData: true,
      data,
      // A reload still running keeps `loading` on until it lands, as before.
      settled: entry.inFlight?.mode !== 'reload',
    });
    this.scheduleIdle(entry);
  }

  /**
   * Shared keys starting with `prefix` are out of date: the ones on screen load again in the
   * background (their data stays), and the rest load when next used. Loaders without a key are
   * never matched.
   */
  invalidate(prefix: string): void {
    for (const entry of this.entries.values()) {
      if (entry.local || !entry.key.startsWith(prefix)) continue;
      if (entry.users > 0 && this.loaderFor(entry)) void this.fetch(entry.key, 'background');
      else entry.stale = true;
    }
  }

  /** Load a shared key before it's shown (hovering a link), unless it is fresh or loading. */
  prefetch<T>(key: string, load: LoadFn<T>, { staleMs = 0 }: { staleMs?: number } = {}): void {
    const entry = this.entry(key);
    if (entry.inFlight) return;
    const { settledAt } = entry.snapshot;
    const fresh =
      entry.snapshot.hasData && !entry.stale && settledAt !== undefined && this.now() - settledAt < staleMs;
    if (fresh) return;
    entry.prefetchLoad = load as LoadFn<unknown>;
    void this.fetch(key, entry.snapshot.hasData ? 'background' : 'reload', { prefetch: entry.users === 0 });
  }

  /** Forget everything (sign-out): stop every request and drop every key nothing shows. */
  clear(): void {
    for (const entry of [...this.entries.values()]) {
      if (entry.inFlight) {
        entry.inFlight.superseded = true;
        entry.inFlight.controller.abort();
        entry.inFlight = null;
      }
      if (entry.idleTimer) clearTimeout(entry.idleTimer);
      entry.idleTimer = null;
      if (entry.users === 0 && entry.listeners.size === 0) {
        this.entries.delete(entry.key);
      } else {
        entry.stale = true;
        this.update(entry, { ...EMPTY });
      }
    }
  }

  /**
   * Forget these shared keys (the remembered person signed out): their data goes, a request stops.
   * Other keys, such as a public page's own loader, carry on.
   */
  forget(keys: readonly string[]): void {
    for (const key of keys) {
      const entry = this.entries.get(key);
      if (!entry) continue;
      if (entry.inFlight) {
        entry.inFlight.superseded = true;
        entry.inFlight.controller.abort();
        entry.inFlight = null;
      }
      if (entry.idleTimer) clearTimeout(entry.idleTimer);
      entry.idleTimer = null;
      if (entry.users === 0 && entry.listeners.size === 0) {
        this.entries.delete(key);
      } else {
        entry.stale = true;
        this.update(entry, { ...EMPTY });
      }
    }
  }

  /**
   * Forget everything, then load again what's on screen (someone else signed in under the pages):
   * `clear` alone leaves a mounted loader empty, since its key hasn't changed.
   */
  reset(): void {
    this.clear();
    for (const entry of [...this.entries.values()]) {
      if (entry.users > 0 && this.loaderFor(entry)) void this.fetch(entry.key, 'reload').catch(() => undefined);
    }
  }

  /** Keys held (for tests, and the launch cache's per-project keys). */
  keys(): string[] {
    return [...this.entries.keys()];
  }

  private entry(key: string): Entry {
    let entry = this.entries.get(key);
    if (!entry) {
      entry = {
        key,
        local: key.startsWith(LOCAL_PREFIX),
        snapshot: EMPTY,
        listeners: new Set(),
        users: 0,
        loaders: new Map(),
        prefetchLoad: null,
        inFlight: null,
        seq: 0,
        stale: false,
        lastUsed: this.now(),
        idleTimer: null,
      };
      this.entries.set(key, entry);
    }
    return entry;
  }

  /** The newest user's loader still meant for this key, else prefetch()'s. */
  private loaderFor(entry: Entry): LoadFn<unknown> | null {
    const getters = [...entry.loaders.values()];
    for (let i = getters.length - 1; i >= 0; i--) {
      const load = getters[i]();
      if (load) return load;
    }
    return entry.prefetchLoad;
  }

  private update(entry: Entry, patch: Partial<LoaderSnapshot>): void {
    entry.snapshot = { ...entry.snapshot, ...patch };
    for (const listener of [...entry.listeners]) listener();
    if (!entry.local) for (const observer of [...this.observers]) observer(entry.key, entry.snapshot);
  }

  private keepAlive(entry: Entry): void {
    entry.lastUsed = this.now();
    if (entry.idleTimer) {
      clearTimeout(entry.idleTimer);
      entry.idleTimer = null;
    }
  }

  /**
   * Once nothing uses a key: stop its request and drop a private key; keep a shared key's data for
   * next time, up to `maxIdleEntries`. Waits a tick, because React StrictMode (and a key moving
   * between two components in one commit) releases and takes a key straight back.
   */
  private scheduleIdle(entry: Entry): void {
    if (entry.users > 0 || entry.listeners.size > 0 || entry.idleTimer) return;
    entry.idleTimer = setTimeout(() => {
      entry.idleTimer = null;
      if (entry.users > 0 || entry.listeners.size > 0 || this.entries.get(entry.key) !== entry) {
        return;
      }
      entry.lastUsed = this.now();
      // A prefetch finishes first; settling schedules this again.
      if (entry.inFlight?.prefetch) return;
      if (entry.inFlight) {
        const flight = entry.inFlight;
        entry.inFlight = null;
        flight.controller.abort();
        // A cached key that never got its answer has nothing worth keeping.
        if (!entry.snapshot.hasData) entry.snapshot = EMPTY;
        else entry.snapshot = { ...entry.snapshot, fetching: false, settled: true };
        entry.stale = true;
      }
      if (entry.local || !entry.snapshot.hasData) {
        this.entries.delete(entry.key);
        return;
      }
      this.evictIdle();
    }, 0);
  }

  private evictIdle(): void {
    const idle = [...this.entries.values()]
      .filter((e) => !e.local && e.users === 0 && e.listeners.size === 0)
      .sort((a, b) => a.lastUsed - b.lastUsed);
    for (let i = 0; i < idle.length - this.maxIdle; i++) this.entries.delete(idle[i].key);
  }
}

/** The app's one store. */
export const loaderStore = new LoaderStore();

/**
 * Mark shared loader keys starting with `prefix` out of date, after a save or a live update: the
 * ones on screen reload in the background, keeping their data; the rest reload when next shown.
 * Debounce it for bursts of live ticks.
 */
export function invalidateLoaders(prefix: string): void {
  loaderStore.invalidate(prefix);
}

/** Start loading a shared key before it's shown, unless it's fresh (younger than `staleMs`). */
export function prefetchLoader<T>(key: string, load: LoadFn<T>, options?: { staleMs?: number }): void {
  loaderStore.prefetch(key, load, options);
}

/** Drop every cached result and load again what's on screen (another person signed in). */
export function resetLoaderCache(): void {
  loaderStore.reset();
}

/** Drop every cached result and stop every request (sign-out). */
export function clearLoaderCache(): void {
  loaderStore.clear();
}

/**
 * Runs `run` every `pollMs` counted from `lastAt` (the last answer), but never while the tab is
 * hidden: a tick that falls due while hidden waits until the tab is shown again, then runs at once.
 * Returns the stop for the effect's cleanup.
 */
export function startPoll({
  pollMs,
  lastAt,
  run,
  now = () => Date.now(),
  doc = typeof document === 'undefined' ? undefined : document,
}: {
  pollMs: number;
  lastAt: number | undefined;
  run: () => void;
  now?: () => number;
  doc?: Pick<Document, 'hidden' | 'addEventListener' | 'removeEventListener'>;
}): () => void {
  let waitingForVisible = false;
  const tick = () => {
    timer = null;
    if (doc?.hidden) {
      waitingForVisible = true;
      return;
    }
    run();
  };
  const onVisibility = () => {
    if (waitingForVisible && !doc?.hidden) {
      waitingForVisible = false;
      run();
    }
  };
  const due = Math.max(0, (lastAt ?? now()) + pollMs - now());
  let timer: ReturnType<typeof setTimeout> | null = setTimeout(tick, due);
  doc?.addEventListener('visibilitychange', onVisibility);
  return () => {
    if (timer) clearTimeout(timer);
    doc?.removeEventListener('visibilitychange', onVisibility);
  };
}

/**
 * Runs `run` now and every `everyMs` while the tab is visible, for pings that tell the server you're
 * here. A hidden tab sends nothing, so a page left open doesn't keep the
 * database awake; showing it again runs `run` at once and restarts the count.
 * Returns the stop for the effect's cleanup.
 */
export function startVisibleInterval({
  everyMs,
  run,
  doc = typeof document === 'undefined' ? undefined : document,
}: {
  everyMs: number;
  run: () => void;
  doc?: Pick<Document, 'hidden' | 'addEventListener' | 'removeEventListener'>;
}): () => void {
  let timer: ReturnType<typeof setInterval> | null = null;
  const pause = () => {
    if (timer) clearInterval(timer);
    timer = null;
  };
  const resume = () => {
    if (timer || doc?.hidden) return;
    run();
    timer = setInterval(() => (doc?.hidden ? pause() : run()), everyMs);
  };
  const onVisibility = () => (doc?.hidden ? pause() : resume());
  resume();
  doc?.addEventListener('visibilitychange', onVisibility);
  return () => {
    pause();
    doc?.removeEventListener('visibilitychange', onVisibility);
  };
}
